<script setup lang="ts">
import KeyIcon from "~icons/ic/outline-wifi";
import type { WiFiParam } from "../../common/types.common";
import { booleanOptions } from "../../data/options";
import { defineActions, defineState } from "../common";
import { FormInputSelect, FormInputText } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface OptionType {
    label: string;
    value: string;
}

type ModelType = WiFiParam;
interface PropsType extends BaseProps<ModelType> {
    wanOptions: OptionType[];
    isDirty?: boolean;
    placeholder?: {
        [K in keyof ModelType]: string;
    };
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    isDirty: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => {
        return model.value.ssid !== undefined;
    },
});

const actions = defineActions(model, props, state);
</script>

<template>
    <FormPanel :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <KeyIcon class="ml-1" />
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-2">
                    {{ props.label }}{{ props.isDirty ? "*" : "" }}
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row gap-4">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <FormInputText v-model="model.ssid" embedMode="embed" label="SSID" :mode="state.nestMode" />
            <FormInputSelect
                v-model="model.visible"
                label="Perlihatkan SSID"
                :mode="state.nestMode"
                :options="booleanOptions"
            />
            <FormInputSelect
                v-model="model.wanBinding"
                embedMode="embed"
                label="WAN Binding"
                :mode="state.nestMode"
                :options="props.wanOptions"
            />
            <FormInputSelect
                v-model="model.useAuth"
                label="Gunakan Kata Sandi"
                :mode="state.nestMode"
                :options="booleanOptions"
            />
            <FormInputText
                v-show="model.useAuth === true"
                v-model="model.password"
                embedMode="embed"
                label="Kata Sandi"
                :mode="state.nestMode"
                :placeholder="model.passwordKnown ? undefined : 'TIDAK DIKETAHUI'"
            />
        </div>
    </FormPanel>
</template>
